import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const DEMO = [
  { email: "admin@erp.com", label: "Super Admin" },
  { email: "branchadmin@erp.com", label: "Branch Admin" },
  { email: "counselor@erp.com", label: "Counselor" },
  { email: "faculty@erp.com", label: "Faculty" },
  { email: "student@erp.com", label: "Student" },
  { email: "parent@erp.com", label: "Parent" },
  { email: "accountant@erp.com", label: "Accountant" },
  { email: "hr@erp.com", label: "HR" },
];

export default function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("admin@erp.com");
  const [password, setPassword] = useState("admin123");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user) {
      const dest = loc.state?.from?.pathname || "/dashboard";
      nav(dest, { replace: true });
    }
  }, [user]);  // eslint-disable-line

  const handle = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav("/dashboard");
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F4F2EE]">
      {/* Left: form */}
      <div className="flex items-center justify-center p-6 md:p-16">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-10 h-10 rounded-lg bg-[#0A66C2] text-white flex items-center justify-center text-lg font-bold">C</div>
            <div>
              <div className="text-lg font-bold">CoachERP</div>
              <div className="text-xs text-[#00000099]">Modern Coaching Center Platform</div>
            </div>
          </div>

          <h1 className="text-3xl font-semibold text-[#000000E6] mb-1">Sign in</h1>
          <p className="text-sm text-[#00000099] mb-8">Stay connected to your students, batches, and revenue.</p>

          <form onSubmit={handle} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs font-semibold text-[#00000099] uppercase tracking-wider mb-1">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E0DFDC] rounded-md focus:border-[#0A66C2] focus:ring-1 focus:ring-[#0A66C2] outline-none text-[15px] bg-white"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#00000099] uppercase tracking-wider mb-1">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E0DFDC] rounded-md focus:border-[#0A66C2] focus:ring-1 focus:ring-[#0A66C2] outline-none text-[15px] bg-white"
                required
              />
            </div>

            {err && <div className="text-sm text-[#CC1016] bg-[#F8D7DA] border border-[#F1AEB5] rounded-md px-3 py-2" data-testid="login-error">{err}</div>}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full btn-primary disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#E0DFDC]">
            <div className="text-xs font-semibold text-[#00000099] uppercase tracking-wider mb-3">Demo accounts (password: admin123)</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  data-testid={`demo-login-${d.label.toLowerCase().replace(/\s+/g,'-')}`}
                  onClick={() => { setEmail(d.email); setPassword("admin123"); }}
                  className="text-left px-3 py-2 rounded-md bg-white border border-[#E0DFDC] hover:border-[#0A66C2] hover:bg-[#EAF3FB]"
                >
                  <div className="text-[13px] font-semibold text-[#000000E6]">{d.label}</div>
                  <div className="text-[11px] text-[#00000099] truncate">{d.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right: brand panel */}
      <div className="hidden md:block relative overflow-hidden">
        <div className="absolute inset-0 bg-[#0A66C2]"></div>
        <div className="absolute inset-0 opacity-25"
             style={{backgroundImage: "url('https://images.unsplash.com/photo-1632988663082-4bac2c1847a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzg0MTMzNjU3fDA&ixlib=rb-4.1.0&q=85')", backgroundSize: "cover", backgroundPosition: "center"}} />
        <div className="relative h-full flex flex-col justify-center p-16 text-white">
          <div className="text-sm font-semibold uppercase tracking-wider opacity-80">All-in-one platform</div>
          <h2 className="text-4xl font-semibold leading-tight mt-3">Run your coaching business, end to end.</h2>
          <p className="mt-4 text-white/85 max-w-md">Admissions, batches, fees, tests, and parent engagement &mdash; unified in one workspace. Built for institutes preparing tomorrow&apos;s leaders.</p>

          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
            <Stat n="10+" l="User roles" />
            <Stat n="25+" l="Modules" />
            <Stat n="99.9%" l="Uptime" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="text-2xl font-bold">{n}</div>
      <div className="text-xs text-white/75 uppercase tracking-wider">{l}</div>
    </div>
  );
}
